import { useEffect, useSyncExternalStore } from 'react'

// The theme choice, and nothing else.
//
// Three states, not two — because "dark mode: on/off" quietly overrides a
// machine that already knew the answer:
//
//   'system'  no data-theme attribute — tokens.css follows prefers-color-scheme
//   'light'   data-theme="light"      — beats an OS dark preference
//   'dark'    data-theme="dark"
//
// 'system' is the default and stays the default. A shop that never touches the
// control keeps whatever the counter machine is set to, including a machine
// that turns dark at dusk.
//
// The palette plumbing already exists in tokens.css. This file only decides
// which of the three states the document is in, persists it, and keeps every
// tab agreeing.
//
// There is no provider. A preference read from localStorage does not need
// React context, and a module store read through useSyncExternalStore works
// outside the shell too — the login card is not wrapped in anything.

export const THEME_STORAGE_KEY = 'tiledesk.theme'
export const THEMES = ['light', 'dark', 'system']
export const DEFAULT_THEME = 'system'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function readStored() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return THEMES.includes(stored) ? stored : DEFAULT_THEME
  } catch {
    // Safari in private mode throws on access, not on write, and a corporate
    // policy can disable storage outright. Either way the control still works
    // for this tab; it just does not survive the reload.
    return DEFAULT_THEME
  }
}

let choice = readStored()
const listeners = new Set()

/**
 * The one place the attribute is written. Light and dark stamp it; system
 * removes it, which is what hands the decision back to prefers-color-scheme.
 */
export function applyThemeToDocument(next) {
  const root = document.documentElement
  if (next === 'light' || next === 'dark') root.setAttribute('data-theme', next)
  else root.removeAttribute('data-theme')

  // index.html sets color-scheme inline so the browser paints its own canvas in
  // the right colour before the stylesheet has been parsed, and clears it again
  // at DOMContentLoaded. This is the belt to that pair of braces: an inline
  // color-scheme:dark outranks @media print and would hand a shop a dark form
  // control on paper. From here tokens.css owns the property.
  root.style.removeProperty('color-scheme')
}

export function getTheme() {
  return choice
}

export function setTheme(next) {
  const value = THEMES.includes(next) ? next : DEFAULT_THEME
  applyThemeToDocument(value)
  if (value === choice) return

  choice = value
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, value)
  } catch {
    // Unwritable storage is not a failure worth a toast: the choice holds for
    // this tab and is simply forgotten on reload.
  }
  for (const listener of listeners) listener()
}

/** Another tab changed the preference — follow it rather than disagreeing. */
function onStorage(event) {
  // key is null when the whole store is cleared.
  if (event.key !== null && event.key !== THEME_STORAGE_KEY) return
  const next = readStored()
  if (next === choice) return
  choice = next
  applyThemeToDocument(next)
  for (const listener of listeners) listener()
}

function subscribe(listener) {
  listeners.add(listener)
  if (listeners.size === 1) window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

function systemPrefersDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

function subscribeSystem(listener) {
  if (typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(DARK_QUERY)
  // Safari below 14 has no addEventListener on a MediaQueryList, and the
  // counter machine is whatever the shop already owns.
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
  }
  query.addListener(listener)
  return () => query.removeListener(listener)
}

/**
 * `theme` is the stored choice — one of light, dark, system.
 * `resolved` is what is actually on screen — light or dark, never system —
 * so a control can say "System (currently dark)" instead of leaving the user
 * to guess which way the device went.
 */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getTheme, () => DEFAULT_THEME)
  const prefersDark = useSyncExternalStore(subscribeSystem, systemPrefersDark, () => false)

  // The pre-paint script in index.html has normally done this already. Doing it
  // again on mount is what keeps the toggle honest where that script did not
  // run — an embedded webview, a stripped index.html, a stale cached shell.
  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme])

  return {
    theme,
    resolved: theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme,
    setTheme,
  }
}
